import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/shared/Layout/Layout';
import Button from '../../../components/shared/Button/Button';
import {
  HeroSection, HeroInner, HeroLeft, HeroRight,
  HeroChip, HeroTitle, HeroSubtitle, HeroTrust, TrustItem,
  HeroCircle, HeroCircleInner, FloatCard,
  SearchSection, SearchInput,
  StatsStrip, StatsInner, StatItem, StatNumber, StatLabel,
  HowSection, HowGrid, HowStep, StepNumber, StepIcon, StepTitle, StepDesc,
  FeaturedSection, SectionHeader, SectionLabel, SectionTitle, SectionDesc,
  ActivityGrid, ActivityCard, ActivityImageWrap, ActivityImage, ActivityBadge,
  ActivityInfo, ActivityTitle, ActivityDescription, ActivityMeta,
  ActivityPrice, ActivityDetail,
  CtaStrip,
} from './Home.styles';

const Home = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredActivities, setFeaturedActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchFeaturedActivities = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/activities?featured=true&limit=6');
        const data = await response.json();
        if (data.success) {
          setFeaturedActivities(data.activities);
          setError('');
        } else {
          setError('Error cargando actividades');
          setFeaturedActivities([]);
        }
      } catch (error) {
        console.error('Error fetching featured activities:', error);
        setError('No se pudo conectar con el servidor.');
        setFeaturedActivities([]);
      } finally {
        setLoading(false);
      }
    };
    fetchFeaturedActivities();
  }, []);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm)}`);
    } else {
      navigate('/search');
    }
  };

  const handleActivityClick = (activityId) => {
    navigate(`/activity/${activityId}`);
  };

  return (
    <Layout footerAttached>
      <HeroSection>
        <HeroInner>
          <HeroLeft>
            <HeroChip>Plataforma de experiencias turísticas</HeroChip>
            <HeroTitle>
              Reserva experiencias <em>increíbles</em> en México
            </HeroTitle>
            <HeroSubtitle>
              Tours, parques, museos y aventuras al alcance de un clic.
              Pago seguro, QR instantáneo.
            </HeroSubtitle>

            <SearchSection>
              <SearchInput
                type="text"
                placeholder="¿Qué experiencia buscas?"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} style={{ flexShrink: 0 }}>
                Buscar
              </Button>
            </SearchSection>

            <HeroTrust>
              <TrustItem>
                <strong>500+</strong>
                <span>Actividades</span>
              </TrustItem>
              <TrustItem>
                <strong>30+</strong>
                <span>Ciudades</span>
              </TrustItem>
              <TrustItem>
                <strong>10k+</strong>
                <span>Viajeros</span>
              </TrustItem>
            </HeroTrust>
          </HeroLeft>

          <HeroRight>
            <HeroCircle>
              <HeroCircleInner>🏖️</HeroCircleInner>
              <FloatCard>
                <span className="dot" style={{ background: 'var(--success)' }} />
                Reserva confirmada
              </FloatCard>
              <FloatCard>
                <span className="dot" style={{ background: 'var(--accent)' }} />
                +120 reservas hoy
              </FloatCard>
            </HeroCircle>
          </HeroRight>
        </HeroInner>
      </HeroSection>

      <StatsStrip>
        <StatsInner>
          <StatItem>
            <StatNumber>500+</StatNumber>
            <StatLabel>Actividades disponibles</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>30+</StatNumber>
            <StatLabel>Ciudades en México</StatLabel>
          </StatItem>
          <StatItem>
            <StatNumber>10,000+</StatNumber>
            <StatLabel>Viajeros felices</StatLabel>
          </StatItem>
        </StatsInner>
      </StatsStrip>

      <HowSection>
        <div className="container">
          <SectionHeader>
            <SectionLabel>Simple y rápido</SectionLabel>
            <SectionTitle>¿Cómo funciona?</SectionTitle>
            <SectionDesc>Reserva en menos de 2 minutos, paga seguro y disfruta</SectionDesc>
          </SectionHeader>
        </div>
        <HowGrid>
          <HowStep>
            <StepNumber>1</StepNumber>
            <StepIcon>🔍</StepIcon>
            <StepTitle>Busca tu experiencia</StepTitle>
            <StepDesc>Explora cientos de actividades filtradas por ciudad, categoría y precio.</StepDesc>
          </HowStep>
          <HowStep>
            <StepNumber>2</StepNumber>
            <StepIcon>💳</StepIcon>
            <StepTitle>Reserva y paga</StepTitle>
            <StepDesc>Pago seguro con tarjeta. Tu dinero se retiene hasta el día del evento.</StepDesc>
          </HowStep>
          <HowStep>
            <StepNumber>3</StepNumber>
            <StepIcon>📱</StepIcon>
            <StepTitle>Muestra tu QR</StepTitle>
            <StepDesc>Recibe tu QR por email. Preséntalo en la entrada y listo.</StepDesc>
          </HowStep>
        </HowGrid>
      </HowSection>

      <FeaturedSection>
        <div className="container">
          <SectionHeader>
            <SectionLabel>Actividades</SectionLabel>
            <SectionTitle>Destacadas esta semana</SectionTitle>
            <SectionDesc>Las mejores experiencias seleccionadas para ti</SectionDesc>
          </SectionHeader>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            Cargando actividades...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p style={{ color: 'var(--danger)', marginBottom: '16px' }}>{error}</p>
            <Button variant="secondary" onClick={() => window.location.reload()}>
              Intentar de nuevo
            </Button>
          </div>
        )}

        {!loading && !error && featuredActivities.length === 0 && (
          <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
            No hay actividades destacadas disponibles aun.
          </div>
        )}

        {!loading && !error && featuredActivities.length > 0 && (
          <ActivityGrid>
            {featuredActivities.map(activity => (
              <ActivityCard key={activity.id} onClick={() => handleActivityClick(activity.id)}>
                <ActivityImageWrap>
                  <ActivityImage
                    src={activity.images?.[0] || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=260&fit=crop'}
                    alt={activity.title}
                  />
                  {activity.type && <ActivityBadge>{activity.type}</ActivityBadge>}
                </ActivityImageWrap>
                <ActivityInfo>
                  <ActivityTitle>{activity.title}</ActivityTitle>
                  <ActivityDescription>{activity.description}</ActivityDescription>
                  <ActivityMeta>
                    <ActivityPrice>
                      ${typeof activity.price === 'number' ? activity.price.toLocaleString() : activity.price}
                    </ActivityPrice>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px' }}>
                      {activity.duration && <ActivityDetail>⏱ {activity.duration}</ActivityDetail>}
                      {activity.location && <ActivityDetail>📍 {activity.location}</ActivityDetail>}
                    </div>
                  </ActivityMeta>
                </ActivityInfo>
              </ActivityCard>
            ))}
          </ActivityGrid>
        )}
      </FeaturedSection>

      <CtaStrip>
        <h2>¿Tienes un negocio turístico?</h2>
        <p>Publica tus actividades y llega a miles de viajeros en México</p>
        <Button
          variant="white"
          size="large"
          onClick={() => navigate('/empresa/register')}
        >
          Registra tu empresa gratis
        </Button>
      </CtaStrip>
    </Layout>
  );
};

export default Home;
